// Single source of truth for the Canadian medical-expense tax credit (METC).
//
// All rates + caps are loaded from /app/frontend/src/lib/tax_constants.json
// which is the ONE canonical file for both frontend and backend. Backend
// (server.py) loads the same JSON at startup so there is zero drift.
//
// To update for a new tax year:
//   1. Edit tax_constants.json (federal_rate, threshold_cap, provincial_rates)
//   2. Restart the backend
//   3. Rebuild the frontend
// That is the only place any number should be changed.
//
// CRA reference: Line 33099 — non-refundable medical expense tax credit.
// Threshold = min(3% × net_income, annual cap)
// Credit    = max(0, eligible_medical - threshold) × (federal_rate + provincial_rate)

import taxConstants from "@/lib/tax_constants.json";

export const TAX_YEAR = taxConstants.tax_year;
export const FEDERAL_RATE = taxConstants.federal_rate;
export const THRESHOLD_CAP = taxConstants.threshold_cap;
export const FALLBACK_INCOME = taxConstants.fallback_income;
export const PROVINCIAL_RATES = taxConstants.provincial_rates;

/**
 * Compute the CRA medical-expense tax credit.
 *
 * @param {Object} args
 * @param {number} args.totalMedicalExpenses - Grand total of all CRA-eligible medical
 *   expenses for the year (travel + Rx + services + insurance premiums).
 * @param {number|null|undefined} args.netIncome - User's net income (from Settings).
 *   If null/0/undefined, we fall back to FALLBACK_INCOME and flag needsIncomeSetup=true.
 * @param {string} args.province - Two-letter province code (e.g. "ON").
 */
export function calcMedicalCredit({ totalMedicalExpenses, netIncome, province }) {
  const total = Number(totalMedicalExpenses) || 0;
  const effectiveIncome = Number(netIncome) > 0 ? Number(netIncome) : FALLBACK_INCOME;
  const needsIncomeSetup = !(Number(netIncome) > 0);
  const provKey = PROVINCIAL_RATES[province] ? province : "ON";
  const provinceInfo = PROVINCIAL_RATES[provKey];

  const threshold = Math.min(effectiveIncome * 0.03, THRESHOLD_CAP);
  const eligible = Math.max(0, total - threshold);
  const combinedRate = FEDERAL_RATE + provinceInfo.rate;
  const credit = eligible * combinedRate;
  const remainingToCredit = Math.max(0, threshold - total);

  return {
    threshold,
    eligible,
    combinedRate,
    credit,
    remainingToCredit,
    needsIncomeSetup,
    provinceInfo,
  };
}
